import { Badge, Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { CompositionGroupTitleWithHover } from '@texas/components/shared/compositionGroup/CompositionGroupTitleWithHover';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface Props {
  data: SupplierInquiry;
}

interface StatusPresentation {
  label: string;
  colorScheme: string;
}

export function InquiryIdentitySection({ data }: Props) {
  const { t } = useTranslation();
  const status = resolveInquiryStatus(data, t);

  return (
    <Box pr={{ lg: 6, '2xl': 8 }} pb={{ base: 5, lg: 0 }}>
      <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" mb={2}>
        {t('inquiry.inquiry')}
      </Text>
      <Flex align="center" gap={3} wrap="wrap">
        <Heading size="md" fontFamily="barlow" overflowWrap="anywhere">
          {data.formattedInquiryNumber}
        </Heading>
        <Badge
          px={2}
          py={0.5}
          borderRadius="md"
          colorScheme={status.colorScheme}
        >
          {status.label}
        </Badge>
      </Flex>
      <Flex align="center" gap={2} mt={2} wrap="wrap" fontSize="sm">
        {data.basedOnGroupVersion != null && (
          <>
            <CompositionGroupTitle
              version={data.basedOnGroupVersion}
              note={data.basedOnGroupName}
              type="option"
              fontSize="sm"
            />
            <Text color="inquiryOverview.mutedText">·</Text>
          </>
        )}
        <CompositionGroupTitleWithHover
          compositionGroupId={data.compositionGroupId}
          version={data.version}
          note={null}
          type="version"
          fontSize="sm"
        />
      </Flex>

      <SimpleGrid columns={2} spacing={5} mt={7}>
        <IdentityField
          label={t('general.branch')}
          value={data.branchName}
          sub={data.branchIdentifier}
        />
        <IdentityField
          label={t('suppliers.supplier')}
          value={data.supplierName}
          sub={data.supplierNumber}
        />
      </SimpleGrid>

      {(data.basedOnId != null || data.matchedOffers.length > 0) && (
        <Flex gap={4} wrap="wrap" mt={5} fontSize="sm">
          {data.basedOnId != null && (
            <AngularNavLink
              to="variant.inquiry.detail"
              navobj={{
                inquiryId: data.basedOnId,
                compositionGroupId: data.compositionGroupPurchaseInfoId,
              }}
            >
              {t('inquiry.basedOn')}{' '}
              {data.basedOnFormattedInquiryNumber ?? data.basedOnId}
            </AngularNavLink>
          )}
          {data.matchedOffers.map((matchedOffer) => (
            <AngularNavLink
              key={matchedOffer.offerGroupId}
              to="variant.offers.group"
              navobj={{
                ...(data.articleId ? { articleId: data.articleId } : {}),
                variantId: data.variantId,
                offerGroupId: matchedOffer.offerGroupId,
              }}
            >
              {matchedOffer.offerNumber
                ? `${t('offer.viewOffer')} - ${matchedOffer.offerNumber}`
                : t('offer.viewOffer')}
            </AngularNavLink>
          ))}
        </Flex>
      )}
    </Box>
  );
}

function resolveInquiryStatus(
  data: SupplierInquiry,
  t: TFunction,
): StatusPresentation {
  if (data.archived != null) {
    return { label: t('general.archived'), colorScheme: 'gray' };
  } else if (data.acceptedDate != null) {
    return { label: t('general.accepted'), colorScheme: 'green' };
  } else if (data.sent) {
    return { label: t('inquiry.sent'), colorScheme: 'blue' };
  } else {
    return { label: t('inquiry.draft'), colorScheme: 'green' };
  }
}

function IdentityField({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string | null;
}) {
  return (
    <Box minW={0}>
      <Text fontSize="sm" color="inquiryOverview.mutedText" mb={1}>
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="inquiryOverview.text"
        noOfLines={2}
      >
        {value}
      </Text>
      {sub && (
        <Text fontSize="xs" color="inquiryOverview.mutedText" mt={1}>
          {sub}
        </Text>
      )}
    </Box>
  );
}
